<template>
  <div class="iai-slider" :class="darkMode && 'dark-mode'">
    <Slider v-model="currentValue" v-bind="$attrs" :min="min" :max="max" :range="isRange" :pt="computedPt" />

    <div class="iai-slider-track"></div>
  </div>
</template>

<script setup lang="ts">
import Slider from "primevue/slider";
import { computed } from "vue";

//modelValue and currentTooltip will be array for an range
const props = withDefaults(
  defineProps<{
    min: number;
    max: number;
    backgroundColor?: string;
    currentTooltip: string | string[];
    darkMode?: boolean;
  }>(),
  {
    backgroundColor: "#ffffff",
  }
);

const currentValue = defineModel<number | number[]>({
  required: true,
});
const isRange = computed(() => Array.isArray(currentValue.value));

const handleCuttingClass = computed(() => {
  if (isRange.value || typeof currentValue.value !== "number") return "";
  const percentage = ((currentValue.value - props.min) / (props.max - props.min)) * 100;
  const fromRight = percentage <= 20;
  const fromLeft = percentage >= 80;
  return fromRight ? "cutting-right" : fromLeft ? "cutting-left" : "";
});

const computedPt = computed(() => {
  if (isRange.value) {
    return {
      root: { class: "inner-root" },
      startHandler: { "tooltip-label": `${(props.currentTooltip as string[])[0]}` },
      endHandler: { "tooltip-label": `${(props.currentTooltip as string[])[1]}` },
    };
  }
  return {
    root: { class: "inner-root" },
    handle: { class: `handle ${handleCuttingClass.value}`, "tooltip-label": props.currentTooltip },
  };
});
</script>

<style lang="scss">
$border-radius: 0.3rem;
$track-height: 0.3rem;

.iai-slider {
  position: relative;
  &-track {
    position: absolute;
    width: 100%;
    top: 50%;
    transform: translateY(-50%);
    background: #d4d4d4;
    height: $track-height;
    border-radius: $border-radius;
  }
  &.dark-mode {
    .iai-slider-track {
      @apply bg-dark-blue;
    }
    .inner-root {
      .p-slider-handle {
        &::before {
          background-color: #162a4a;
          font-size: 14px;
          border-radius: 92px;
          color: #b9bfc9;
          padding: 10px 21px;
          margin-top: -0.5rem;
          line-height: 19.6px;
          white-space: nowrap;
          top: calc(-200% - 0.5rem);
        }

        &::before {
          margin-top: -1rem;
        }
        &.cutting-right::before {
          transform: translateX(0);
        }
        &.cutting-left::before {
          transform: translateX(-100%);
        }
      }
      .p-slider-range {
        background-color: #314f80 !important;
      }
      .p-slider-handle {
        background-color: white !important;
        border: 1.5px solid #0c1b33 !important;
        &:hover {
          background-color: #314f80 !important;
        }
        &:focus {
          box-shadow: 0 0 0 0.1rem white !important;
        }
      }
    }
  }
  .inner-root {
    z-index: 1;
    cursor: pointer;
    height: $track-height !important;
    padding-top: 0.3rem !important;
    padding-bottom: 0.3rem !important;
    background-clip: content-box !important;
    background: transparent !important;
    box-sizing: content-box !important;

    .p-slider-handle {
      border-color: #ff5470 !important;
      background-color: #ff5470 !important;
      height: 1rem;
      width: 1rem;
      margin-top: -0.5rem;
      margin-left: -0.5rem;

      &:focus {
        box-shadow: 0 0 0 0.2rem #ff4a6587 !important;
        cursor: grabbing;
      }
    }
    .p-slider-range {
      border-radius: $border-radius 0 0 $border-radius;
      background-color: #ff5470 !important;
      height: inherit !important;
      top: 50% !important;
      transform: translateY(-50%);
    }
    .p-slider-handle::before {
      content: attr(tooltip-label);
      position: absolute;
      top: 14px;
      transform: translateX(-50%);
      left: 50%;
      min-width: max-content;
      padding: 0.25rem 0.5rem;
      font-size: 1rem;
      color: var(--color-theme-1);
    }
  }
}
</style>
